import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import InterfaceTable from "../../Table/InterfaceTable";
import { CustomCard } from "../../Utils/UI/Text";
import { message, DatePicker } from "antd";
import Formatter from "../../Data/Domain/Formatter";
import Storage from "../../Data/Domain/Storage";
import dayjs from "dayjs";
import "./Documents.css";
import {
  getDocumentsSelector,
  getDocumentsCountSelector,
} from "../Infrastructure/Documents.reducer";
import {
  getUserDocuments,
  getDocumentsByUser,
} from "../Application/Documents.bussines";
import Document from "../../DocumentUpload/Domain/Document";
import Searcher from "../../Projects/UI/Searcher";

const { RangePicker } = DatePicker;

const OwnerDocumentsTable = () => {
  const documents = useSelector(getDocumentsSelector);
  const totalFromStore = useSelector(getDocumentsCountSelector);

  const total =
    Number.isFinite(totalFromStore) && totalFromStore >= 0
      ? totalFromStore
      : Array.isArray(documents)
      ? documents.length
      : 0;

  const [values, setValues] = useState({ page: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Callback to receive the project ID from the Searcher component
  const handleProjectSelect = (projectId) => {
    console.log("handleProjectSelect called with projectId:", projectId);
    setSelectedProjectId(projectId);
  };

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const role = new Storage("rol").getItem();
  const isSustainability = role === "sustainability";

  const fetchData = useCallback(
    async ({
      page = values.page,
      limit = values.limit,
      sortField,
      sortOrder,
      ...rest
    } = {}) => {
      try {
        if (mountedRef.current) setLoading(true);
        const userId = new Storage("id").getItem();

        const params = {
          userId,
          page,
          limit,
          field_sort: sortField || "date",
          sort: sortOrder || "descending",
          ...(dateRange[0] && {
            start_date: dateRange[0].format("YYYY-MM-DD"),
          }),
          ...(dateRange[1] && {
            finish_date: dateRange[1].format("YYYY-MM-DD"),
          }),
          ...(typeFilter && typeFilter.length > 0 && { type: typeFilter }),
          ...(selectedProjectId && { projectId: selectedProjectId }),
          ...rest,
        };

        console.log(
          "fetchData params before calling getDocumentsByUser:",
          params
        );
        await getDocumentsByUser(params);
      } catch {
        if (mountedRef.current) message.error("Error al cargar documentos");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [values.page, values.limit, typeFilter, dateRange, selectedProjectId]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedProjectId) {
      console.log("selectedProjectId changed to:", selectedProjectId);
      const userId = new Storage("id").getItem();
      getDocumentsByUser({
        userId,
        page: 0,
        limit: 10,
        field_sort: "date",
        sort: "descending",
        projectId: selectedProjectId,
      });
    }
  }, [selectedProjectId]);

  const handleRequest = (newPagination = {}, filters = {}, sorter = {}) => {
    const { current = values.page + 1, pageSize = values.limit } =
      newPagination;
    const { field: sortField, order: sortOrder } = sorter;

    const newTypeFilter = Array.isArray(filters?.type) ? filters.type : [];
    const newProjectFilter = Array.isArray(filters?.projectName)
      ? filters.projectName
      : [];

    const nextPage = filters ? 0 : current - 1;
    setTypeFilter(newTypeFilter);
    setProjectFilter(newProjectFilter);

    const clean = Formatter.cleanParamsFromTableToAPI({
      limit: pageSize,
      sortField,
      sortOrder,
      page: nextPage,
    });

    fetchData({
      ...clean,
      type: newTypeFilter.length > 0 ? newTypeFilter : undefined,
      project: newProjectFilter.length > 0 ? newProjectFilter : undefined,
      start_date: dateRange?.[0] || undefined,
      finish_date: dateRange?.[1] || undefined,
    });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || []);
  };

  const rows = useMemo(
    () =>
      (documents ?? []).map((d) => ({
        ...d,
        projectName:
          d?.projectName ?? d?.project_name ?? d?.project?.name ?? "—",
      })),
    [documents]
  );

  const typeOptions = useMemo(
    () => [
      { text: "ID de Compañía", value: Document.COMPANY_ID },
      { text: "Inicio de actividades", value: Document.LOCAL_TAX_AUTHORITY },
      { text: "Acta constitutiva", value: Document.REGISTRATION },
      {
        text: "Documento legal que acredita la representación legal",
        value: Document.LEGAL_REPRESENTATION,
      },
      {
        text: "Certificado de vigencia de Sociedad",
        value: Document.LAST_STATEMENT,
      },
      {
        text: "Cédula de identidad de representante legal",
        value: Document.PERSONAL_ID,
      },
      {
        text: "Datos de cuenta bancaria",
        value: Document.BANK_ACCOUNT_DETAILS,
      },
      { text: "Foto de perfil", value: Document.PROFILE_PICTURE },
      {
        text: "Archivo de gestión de carbono",
        value: Document.CARBON_MANAGEMENT_FILE,
      },
    ],
    []
  );

  const projectOptions = useMemo(() => {
    const unique = Array.from(
      new Set(
        (documents ?? [])
          .map((d) => d?.projectName ?? d?.project_name ?? d?.project?.name)
          .filter(Boolean)
      )
    );
    return unique.map((name) => ({
      text: name,
      value: name,
    }));
  }, [documents]);

  const columns = useMemo(() => {
    const base = [
      {
        title: "Tipo",
        dataIndex: "type",
        key: "type",
        filters: typeOptions.map((t) => ({
          text: t.text,
          value: t.value,
        })),
        filteredValue:
          Array.isArray(typeFilter) && typeFilter.length > 0
            ? typeFilter
            : null,
        render: (type) => {
          const found = typeOptions.find((t) => t.value === type);
          return found ? found.text : "—";
        },
      },
      {
        title: "URL",
        dataIndex: "url",
        key: "url",
        render: (url) =>
          url ? (
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          ) : (
            "—"
          ),
      },
      {
        title: "Fecha de Creación",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        render: (_, r) =>
          r.createdAt ? dayjs(r.createdAt).format("DD/MM/YYYY HH:mm") : "—",
      },
    ];

    if (isSustainability) {
      base.splice(1, 0, {
        title: "Nombre del Proyecto",
        dataIndex: "projectName",
        key: "projectName",
        filters: projectOptions,
        filteredValue:
          Array.isArray(projectFilter) && projectFilter.length > 0
            ? projectFilter
            : null,
        render: (text) => text || "—",
      });
    }

    return base;
  }, [
    isSustainability,
    typeOptions,
    typeFilter,
    projectOptions,
    projectFilter,
  ]);

  return (
    <div className="documents-page">
      {/* Project Searcher (Visible only for sustainability role) */}
      {isSustainability && <Searcher onProjectSelect={handleProjectSelect} />}
      <CustomCard>
        <div className="documents-filters">
          <RangePicker
            onChange={handleDateChange}
            placeholder={["Fecha de inicio", "Fecha de fin"]}
            style={{ width: "100%" }}
          />
        </div>

        <div className="documents-table-wrap">
          <InterfaceTable
            rowKey="id"
            data={rows}
            columns={columns}
            loading={loading}
            onChange={handleRequest}
            pagination={{
              current: values.page + 1,
              pageSize: values.limit,
              total,
              position: ["bottomCenter"],
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["5", "10", "20", "25", "50", "100"],
              showTotal: (t, [s, e]) => `${s}-${e} de ${t}`,
            }}
            locale={{ emptyText: "No Data" }}
          />
        </div>
      </CustomCard>
    </div>
  );
};

export default OwnerDocumentsTable;
